// src/pages/EditInventoryAudit.tsx
import React, { useState, useEffect, FormEvent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

interface Report {
  id: number;
  created_at: string;
}

interface InventoryAuditData {
  id?: number;
  item: string;
  quantity: number;
  genuine: boolean;
  notes: string;
}

const EditInventoryAudit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [rows, setRows] = useState<InventoryAuditData[]>([]);
  const [deletedIds, setDeletedIds] = useState<number[]>([]);
  const [newestReportId, setNewestReportId] = useState<number | null>(null);
  const [error, setError] = useState("");

  // 1. Fetch or create the latest report
  useEffect(() => {
    fetch(`/api/dealers/${id}/reports`)
      .then(res => res.json())
      .then((data: Report[]) => {
        if (data.length) {
          setNewestReportId(data[0].id);
        } else {
          fetch(`/api/dealers/${id}/reports`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          })
            .then(r => r.json())
            .then((nr: Report) => setNewestReportId(nr.id))
            .catch(() => setError('Unable to create report'));
        }
      })
      .catch(() => setError('Unable to fetch reports'));
  }, [id]);

  // 2. Load rows when report ID available
  useEffect(() => {
    if (!newestReportId) return;
    fetch(`/api/reports/${newestReportId}/inventory-audit`)
      .then(async res => {
        if (!res.ok) throw new Error(`Load failed: ${res.status}`);
        return res.json();
      })
      .then((data: any[]) => {
        if (Array.isArray(data) && data.length) {
          setRows(data.map(rec => ({
            id: rec.id,
            item: rec.item || "",
            quantity: rec.quantity ?? 0,
            genuine: rec.genuine ?? false,
            notes: rec.notes || "",
          })));
        } else {
          setRows([{ item: "", quantity: 0, genuine: false, notes: "" }]);
        }
      })
      .catch(err => {
        console.error(err);
        setRows([]);
      });
  }, [newestReportId]);

  const handleChange = (
    idx: number,
    field: keyof InventoryAuditData,
    val: string | boolean
  ) => {
    setRows(prev => {
      const copy = [...prev];
      (copy[idx] as any)[field] = field === 'quantity' ? Number(val) : val;
      return copy;
    });
  };

  const addRow = () => {
    setRows(prev => [...prev, { item: "", quantity: 0, genuine: false, notes: "" }]);
  };

  const deleteRow = (idx: number) => {
    setRows(prev => {
      const rec = prev[idx];
      if (rec.id) setDeletedIds(ids => [...ids, rec.id!]);
      return prev.filter((_, i) => i !== idx);
    });
  };

    const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!newestReportId) {
      setError("No report found");
      return;
    }

    try {
      // Prepare payload: array of items (new and existing)
       const payload = rows.map(r => ({
   id: r.id,
   dealer_id: Number(id),        // ← inject the dealer’s ID here
   item:     r.item,
   quantity: r.quantity,
   genuine:  r.genuine,
   notes:    r.notes,
 }));

      const res = await fetch(
        `/api/reports/${newestReportId}/inventory-audit`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Update failed');
      }

      // On success, navigate back
      navigate(`/dealer/${id}`);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Unknown error');
    }
  };


  // Styles
  const containerStyle: React.CSSProperties = { backgroundColor: '#F0F5F0', minHeight: '100vh', padding: '20px' };
  const cardStyle: React.CSSProperties = { backgroundColor: '#FFFFFF', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)', maxWidth: '800px', margin: '30px auto', padding: '30px' };
  const headerStyle: React.CSSProperties = { textAlign: 'center', color: '#e41e26', borderBottom: '2px solid #e41e26', paddingBottom: '10px', marginBottom: '20px', fontSize: '24px' };
  const labelStyle: React.CSSProperties = { display: 'block', marginBottom: '5px', fontWeight: 'bold' };
  const inputStyle: React.CSSProperties = { width: '100%', padding: '8px', marginBottom: '10px', borderRadius: '4px', border: '1px solid #ccc' };
  const buttonStyle: React.CSSProperties = { padding: '5px 10px', backgroundColor: '#e41e26', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '10px' };
  const deleteButtonStyle: React.CSSProperties = { ...buttonStyle, backgroundColor: 'transparent', color: '#e41e26', fontSize: '18px', padding: '0 8px' };

  return (
    <div style={containerStyle}>
      <Navbar />
      <div style={cardStyle}>
        <h1 style={headerStyle}>Edit Inventory Audit</h1>
        {error && <p style={{ color: 'red', textAlign: 'center' }}>{error}</p>}
        <form onSubmit={handleSubmit}>
          {rows.map((r, idx) => (
            <div key={r.id != null ? `${r.id}-${idx}` : `new-${idx}`} style={{ marginBottom: '15px', border: '1px solid #ccc', padding: '10px', borderRadius: '4px', position: 'relative' }}>
              <button type="button" onClick={() => deleteRow(idx)} style={{ ...deleteButtonStyle, position: 'absolute', top: '10px', right: '10px' }} title="Delete this row">
                🗑️
              </button>

              <label style={labelStyle}>Item:</label>
              <input value={r.item} onChange={e => handleChange(idx, 'item', e.target.value)} style={inputStyle} />

              <label style={labelStyle}>Quantity:</label>
              <input type="number" value={r.quantity} onChange={e => handleChange(idx, 'quantity', e.target.value)} style={inputStyle} />

              <label style={labelStyle}>Genuine (Yes/No):</label>
              <select value={r.genuine ? 'Yes' : 'No'} onChange={e => handleChange(idx, 'genuine', e.target.value === 'Yes')} style={inputStyle}>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>

              <label style={labelStyle}>Notes:</label>
              <input value={r.notes} onChange={e => handleChange(idx, 'notes', e.target.value)} style={inputStyle} />
            </div>
          ))}

          <button type="button" onClick={addRow} style={buttonStyle}>Add Row</button>
          <br /><br />
          <button type="submit" style={buttonStyle}>Save</button>
          <button type="button" style={buttonStyle} onClick={() => navigate(`/dealer/${id}`)}>Cancel</button>
        </form>
      </div>
    </div>
  );
};

export default EditInventoryAudit;
